import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import AlertService from "../../../../services/AlertService";
import ReviewMCQExamService from "../../../../services/ReviewMCQExamService";
import MCQExamService from "../../../../services/MCQExamService";
import AuthContext from "../../../../auth/AuthContext";
import ViewAssessmentMCQExamQuestion from "./ViewAssessmentMCQExamQuestion";
import ChronometerIcon from '../../../../assets/icons/ChronoMeter.svg';
import QuestionMarkIcon from '../../../../assets/icons/QuestionMark.svg';
import PreviousIcon from '../../../../assets/icons/Previous.svg';
import WhitePreviousIcon from '../../../../assets/icons/WhitePreviousIcon.svg';
import NextIcon from '../../../../assets/icons/Next.svg';
import WhiteNextIcon from '../../../../assets/icons/WhiteNextIcon.svg';
import CalcIcon from '../../../../assets/icons/CalcC.svg';
import CancelIcon from '../../../../assets/icons/Cancel.svg';
import { Button } from "react-bootstrap";
import { Skeleton } from "primereact/skeleton";
import { ThemeContext } from "../../../../theme/ThemeContext";
import { FaCheck, FaTimes } from "react-icons/fa";

const ViewAssessmentMCQExam = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { assessmentName } = useParams();
    const [loading, setLoading] = useState(false);
    const [assessmentExam, setAssessmentExam] = useState();
    const [selectedIndex, setSelectedIndex] = useState();
    const [selectedCardId, setSelectedCardId] = useState(0);
    const [selectedCard, setSelectedCard] = useState({});
    const [questions, setQuestions] = useState([]);
    const [visible, setVisible] = useState(false);
    const [reason, setReason] = useState();
    const [timerText, setTimerText] = useState('00:00:00');
    const authContext = useContext(AuthContext);
    const scrollRef = useRef();
    const { theme } = useContext(ThemeContext);
    const cancelControllerRef = useRef(null);
    const isDark = theme === "dark";
    const assessmentAccess = authContext.getPermissions("assessments");
    const [cancelLoading, setCancelLoading] = useState(false);
    if (assessmentAccess.filter((f) => f.access.includes(1)).length === 0) {
        navigate("/");
    }

    const scrollToItem = () => {
        if (scrollRef.current) {
            const parentContainer = scrollRef.current.parentElement;
            const itemRect = scrollRef.current.getBoundingClientRect();
            const parentRect = parentContainer.getBoundingClientRect();

            const isVisible = (
                itemRect.top >= parentRect.top &&
                itemRect.bottom <= parentRect.bottom &&
                itemRect.left >= parentRect.left &&
                itemRect.right <= parentRect.right
            );

            if (!isVisible) {
                scrollRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest'
                });
            }
        }
    };

    const minutesToTime = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    }

const loadQuestions = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await ReviewMCQExamService.GetByExam(
        state?.id,
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setAssessmentExam(data.data);
        setQuestions(data.data.questions ?? []);
        if (data.data.questions.length > 0) {
          setSelectedCardId(data.data.questions[0].id);
          setSelectedCard(data.data.questions[0]);
        }
        setSelectedIndex(0);
        setTimerText(`${minutesToTime(data.data.duration)}:00`);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext, state?.id]);

useEffect(() => {
    const controller = new AbortController(); 
    if (state?.id) {
      loadQuestions(controller.signal); 
    }
    return () => controller.abort();
  }, [loadQuestions, state.id]);

const onCancelHandler = () => {
    navigate(`/masters-assessments/${assessmentName}/mcq-attendees`, { 
        state: { id: state?.assessmentId } // assessmentId = master assessment id ✅
    });
};



const onCancelExam = async () => {
  if (cancelLoading) return;

  if (!reason) {
    AlertService.error("Please provide reason for cancellation.");
    return;
  }

  setCancelLoading(true); 

  if (cancelControllerRef.current) {
    cancelControllerRef.current.abort();
  }

  const controller = new AbortController();
  cancelControllerRef.current = controller;

  try {
    const payload = {
      examId: assessmentExam.id,
      cancelReason: reason,
      cancelledBy: authContext.userId,
    };

    const result = await MCQExamService.Cancel(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (result.isSuccess) {
      AlertService.success("Exam has been cancelled successfully.");
      onCancelHandler();

    } else {
      AlertService.error(result.errorMessage);
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    AlertService.error("Failed to cancel exam");
  } finally {
    setCancelLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    cancelControllerRef.current?.abort();
  };
}, []);

    const onCardClick = (data, i) => {
        let preId = selectedCardId;
        setSelectedCardId(data.id);
        setSelectedCard(data);
        setSelectedIndex(i);
        if (preId === undefined || preId === null || preId === 0) {
            setTimeout(() => {
                scrollToItem();
            }, 10);
        }
    };

    const RecalculateScore = async () => {
        let result = await MCQExamService.RecalculateScore(state?.id, authContext);
        if (result.isSuccess) {
            AlertService.success("Score has been recalculated successfully.");
            loadQuestions();
        } else {
            AlertService.error(result.errorMessage);
        }
    };

    const moveToPrevious = (e) => {
        setSelectedIndex((prev) => {
            if (prev > 0) {
                return prev - 1;
            } else {
                return 0;
            }
        });
    };

    const moveToNext = (e) => {
        setSelectedIndex((prev) => {
            if (prev === questions.length - 1) {
                return prev;
            } else {
                return prev + 1;
            }
        });
    };

    useEffect(() => {
        if (questions && questions.length > 0) {
            setSelectedCardId(questions[selectedIndex].id);
            setSelectedCard(questions[selectedIndex]);
            setTimeout(() => {
                scrollToItem();
            }, 10);
        }
    }, [selectedIndex, questions]);

    return (
        <Fragment>
            <div className="container-fluid question-container mt-3">
                <div className="row d-none d-md-flex">
                    <div className="col-md-2 col-sm-4 scroll-no-buttons" style={{ maxHeight: '82vh', overflowY: 'auto' }}>
                        {loading ? (
                            Array.from({ length: 20 }).map((_, idx) => (
                                <div key={idx} >
                                    <div
                                        className="mt-1 text-center">
                                        <div className="pt-1 pb-1" title='Loading'>
                                            <Skeleton width="100%" height="2rem" />
                                        </div>
                                    </div>
                                </div>
                            ))) : (questions &&
                                questions.length > 0 &&
                                questions.map((q, i) => (
                                    <div
                                        key={i}
                                        ref={q.id === selectedCardId ? scrollRef : null}
                                    >
                                        
                                                            <div
                                                              className={`
                                                mt-1 text-center cursor-pointer
                                                ${q.id === (selectedCardId ?? 0) ? "quiz-active-card" : "quiz-card"}
                                                ${q.isCorrectAnswer ? "list-correct-answer" : "list-wrong-answer"}
                                            `}
                                                              onClick={(e) => {
                                                                e.preventDefault();
                                                                onCardClick(q, i);
                                                              }}
                                                            >
                                                              <div
                                                                className={`pt-1 pb-1 cursor-pointer ${
                                                                  q.isCorrectAnswer
                                                                    ? "list-correct-answer"
                                                                    : "list-wrong-answer"
                                                                }`}
                                                                title={q.question}
                                                              >
                                                                {q.isCorrectAnswer && <FaCheck className="me-2 mb-1 inline-block" />}
                                                {q.isCorrectAnswer === false && <FaTimes className="me-2 mb-1 inline-block" />}
                                                                Question {i + 1}
                                                              </div>
                                                            </div>
                                    </div>
                                )))}
                    </div>
                    <div className="col-md-10 col-sm-8 view">
                        {loading ? (
                            <div className="d-flex bd-highlight">
                                <div className='text-primary mt-2'>
                                    <Skeleton size="5rem" shape="circle" />
                                </div>
                                <div className="p-2 mt-auto mb-auto" style={{ color: '#104E66', width: '30%' }}>
                                    <div className="title">
                                        <Skeleton width="80%" height="1.5rem" />
                                    </div>
                                    <div className="title pt-1"><Skeleton width="85%" height="1.5rem" /></div>
                                    <div className="title pt-1"><Skeleton width="60%" height="1.5rem" /></div>
                                </div>

                                <div className="ms-auto" style={{ width: '40%' }}>
                                    <div className="d-flex flex-column align-items-end" style={{ width: '100%' }}>
                                        <div className="fw-light pt-1" title='Questions'>
                                            <Skeleton width="10rem" height="1.5rem" />
                                        </div>
                                        <div className="fw-light pt-1" title='Duration'>
                                            <Skeleton width="12rem" height="1.5rem" />
                                        </div>
                                        <div className="fw-light pt-1">
                                            <Skeleton width="30rem" height="1.5rem" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (<div className="d-flex bd-highlight flex-wrap flex-md-nowrap align-items-start mb-3 gap-3 quiz-header-responsive">
                            <div className='text-success'>
                                <svg width="120" height="120" viewBox="0 0 120 120">
                                    <circle
                                        cx="60"
                                        cy="60"
                                        r="50"
                                        fill="none"
                                        stroke="#FF9900"
                                        strokeWidth="4"
                                        strokeLinecap="round"
                                        className="progress-circle"
                                    />
                                    <text
                                        x="60"
                                        y="67"
                                        textAnchor="middle"
                                        fontSize="18"
                                        fontWeight="bold"
                                        fill="#FF9900"
                                    >
                                        {timerText}
                                    </text>
                                </svg>
                            </div>
                            <div className="p-2 mt-auto mb-auto" style={{ color: isDark ? "#ffffff" : "#104E66" }}>
                                <div className="title">Assessment Name : {assessmentExam && assessmentExam.assessmentName}</div>
                                <div className="title pt-2">Course : {assessmentExam && assessmentExam.courseName}</div>
                                <div className="title pt-2">
                                    <button
                                        onClick={RecalculateScore}
                                        className='p-0'
                                        style={{
                                            background: 'none',
                                            border: 'none',

                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            font: 'inherit',
                                            color: isDark ? "#ffffff" : "#104E66",
                                        }}
                                    ><img
                                            src={CalcIcon}
                                            alt="Re-Calculate Score"
                                            className="ms-0 me-2 "

                                        />
                                        Re-Calculate
                                    </button>
                                </div>
                            </div>

                            <div className="ms-auto">
                                <div className="d-flex flex-column align-items-end">
                                    <div className="fw-light pt-2" title='Questions'>
                                        <img
                                            src={QuestionMarkIcon}
                                            alt="Questions"
                                            className="me-1 mb-1"
                                        /> Questions : {assessmentExam && assessmentExam.totalMCQQuestions}
                                    </div>
                                    <div className="fw-light pt-2" title='Duration'>
                                        <img
                                            src={ChronometerIcon}
                                            alt="Duration"
                                            className="me-1 mb-1"
                                        /> Duration : {minutesToTime(assessmentExam && assessmentExam.duration)}
                                    </div>
                                    <div className="fw-light pt-2" title='Cancel Exam'>
                                        <button
                                            onClick={() => setVisible(true)}
                                            className='text-primary p-0'
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                font: 'inherit'
                                            }}
                                        ><img
                                                src={CancelIcon}
                                                alt="Cancel Exam"
                                                className="me-2 "
                                            />
                                            Cancel Exam
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>)}
                        {loading ? (
                            <div>
                                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                                    <Skeleton width="100%" height="2px" />
                                </div>
                                <div className="d-flex ps-3 pe-3 mb-3">
                                    <Skeleton width="60%" height="2rem" />
                                </div>
                                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                                    <Skeleton width="100%" height="2px" />
                                </div>

                                <div className="d-flex ps-3 pe-3 mb-3">
                                    <center style={{ width: '5%' }} className="pe-1">
                                        <Skeleton width="2rem" height="2rem" shape='circle' />
                                    </center>
                                    <div style={{ width: '100%' }} className="pe-1">
                                        <Skeleton width="40%" height="2rem" />
                                    </div>
                                </div>

                                <div className="d-flex ps-3 pe-3 mb-3">
                                    <center style={{ width: '5%' }} className="pe-1">
                                        <Skeleton width="2rem" height="2rem" shape='circle' />
                                    </center>
                                    <div style={{ width: '100%' }} className="pe-1">
                                        <Skeleton width="35%" height="2rem" />
                                    </div>
                                </div>

                                <div className="d-flex ps-3 pe-3 mb-3">
                                    <center style={{ width: '5%' }} className="pe-1">
                                        <Skeleton width="2rem" height="2rem" shape='circle' />
                                    </center>
                                    <div style={{ width: '100%' }} className="pe-1">
                                        <Skeleton width="30%" height="2rem" />
                                    </div>
                                </div>

                                <div className="d-flex ps-3 pe-3 mb-3">
                                    <center style={{ width: '5%' }} className="pe-1">
                                        <Skeleton width="2rem" height="2rem" shape='circle' />
                                    </center>
                                    <div style={{ width: '100%' }} className="pe-1">
                                        <Skeleton width="45%" height="2rem" />
                                    </div>
                                </div>

                                <div className="d-flex ps-3 pe-3 mb-3">
                                    <center style={{ width: '5%' }} className="pe-1">
                                        <Skeleton width="2rem" height="2rem" shape='circle' />
                                    </center>
                                    <div style={{ width: '100%' }} className="pe-1">
                                        <Skeleton width="40%" height="2rem" />
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                                    <Skeleton width="100%" height="2px" />
                                </div>

                                <div className="d-flex justify-content-between ps-3 pe-3 mb-3">
                                    <Skeleton width="12rem" height="2rem" />
                                    <Skeleton width="12rem" height="2rem" />
                                </div>
                            </div>
                        ) : (
                            <ViewAssessmentMCQExamQuestion
                                index={selectedIndex}
                                input={selectedCard}
                            ></ViewAssessmentMCQExamQuestion>
                        )}
                        {!loading && questions && questions.length > 0 && (
                            <div className="d-flex">
                                <div className="pt-3 ps-2">
                                    {selectedIndex > 0 && (
                                        <button
                                            className="p-0 btn btn-outline"
                                            onClick={moveToPrevious}
                                        >
                                            <div className="d-flex">
                                                <img
                                                    src={isDark ? WhitePreviousIcon : PreviousIcon}
                                                    alt="Previous"
                                                    style={{ width: '40px', marginLeft: '-3px' }}
                                                />
                                                <div className="me-4 mt-2 ms-3">Previous</div>
                                            </div>
                                        </button>
                                    )}
                                </div>
                                <div className="ms-auto pt-3 pe-2">
                                    {selectedIndex < questions.length - 1 && (
                                        <button

                                            className="p-0 btn btn-outline"
                                            onClick={moveToNext}
                                        >
                                            <div className="d-flex m-auto">
                                                <div className="me-4 mt-2 ms-3">Next</div>
                                                <img
                                                    src={isDark ? WhiteNextIcon : NextIcon}
                                                    style={{ width: '40px', marginRight: '-3px' }}
                                                    alt="Previous"
                                                />
                                            </div>
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Dialog
                header="Reason to cancel this Exam?"
                visible={visible}
                style={{ width: "50vw" }}
                onHide={() => {
                    if (!visible) return;
                    setVisible(false);
                }}
            >
                <div className="row m-0 p-0">
                    <div className="col-12 mb-2 mt-2">
                        <textarea
                            className="form-control no-resize cancel-reason custom-input-color"
                            maxLength={250}
                            defaultValue={reason}
                            onChange={(e) => setReason(e.target.value)}
                        ></textarea>
                    </div>
                    <div className="d-flex mt-2">
                        <Button
                            variant="outline-secondary"
                            className="px-4 px-md-5 py-2 me-4"
                            onClick={() => {
                                setReason("");
                                setVisible(false);
                            }}
                             disabled={cancelLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            className="ms-auto px-4 px-md-5 py-2"
                            onClick={onCancelExam}
                              disabled={cancelLoading}
                        >Submit</Button>
                    </div>
                </div>
            </Dialog>
        </Fragment >
    );
};

export default ViewAssessmentMCQExam;
